import { createRouter as createTanStackRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'

/* TanStack Router false positive (TanStack/router#7691): linking to a path
   that has both a layout and an index route ("/", "/admin") always warns,
   because the destination resolves to the layout while the generated URL
   matches the index. Dev-only warning; filter it until the issue is fixed. */
if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
  const originalWarn = console.warn.bind(console)
  console.warn = (...args: unknown[]) => {
    if (
      typeof args[0] === 'string' &&
      args[0].startsWith('Generated path "') &&
      args[0].includes('params.stringify')
    ) {
      return
    }
    originalWarn(...args)
  }
}

function getCauseChain(err: unknown): unknown[] {
  const chain: unknown[] = []
  let cur: unknown = err
  while (cur && chain.length < 8) {
    chain.push(cur)
    cur = (cur as { cause?: unknown })?.cause
  }
  return chain
}

function DevErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  const chain = getCauseChain(error)
  return (
    <div style={{ padding: 24, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, lineHeight: 1.5 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <strong style={{ color: '#c92a2a', fontSize: 16 }}>Application error</strong>
        <button type="button" onClick={reset} style={{ padding: '4px 10px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
      {chain.map((e, i) => {
        const isErr = e instanceof Error
        const name = isErr ? e.name : typeof e
        const message = isErr ? e.message : String(e)
        const stack = isErr ? e.stack : undefined
        return (
          <details key={i} open={i === 0} style={{ marginBottom: 12, border: '1px solid #e0e0e0', borderRadius: 4, padding: 8 }}>
            <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
              <strong>{i === 0 ? 'Error' : `Caused by [${i}]`}:</strong> {name} — {message}
            </summary>
            {stack && (
              <pre style={{ marginTop: 8, whiteSpace: 'pre-wrap', overflowX: 'auto', background: '#fafafa', padding: 8, borderRadius: 4 }}>
                {stack}
              </pre>
            )}
          </details>
        )
      })}
    </div>
  )
}

export function getRouter() {
  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: DevErrorComponent,
  })

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
